import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { bool, or } from '@ember/object/computed';
import Controller from '@ember/controller';
import { task, timeout, waitForQueue } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import Ember from 'ember';
import ReleaseNotificationService from 'cio/services/release-notification';
import AuthService from 'cio/services/auth';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import AlertsService from 'cio/services/alerts';
import CrumbsService from 'cio/services/crumbs';
import EnvironmentService from 'cio/services/environment';
import MasqService from 'cio/services/masq';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentModel from 'cio/models/environment';
import { tracked } from '@glimmer/tracking';
import { unixNowMilliseconds } from 'cio/utils/time';
import config from 'cio/config/environment';
import EventBusService, { CONFIRM_TRANSITION } from 'cio/services/event-bus';
import BillingService from 'cio/services/billing';
import ConfirmChangesService from 'cio/services/confirm-changes';
import HydraService from 'cio/services/hydra';
import Transition from '@ember/routing/-private/transition';

import { setNavCollapsed } from '@customerio/hydra-nav-state';
import { registerDestructor } from '@ember/destroyable';
import { sortByCaseInsensitive } from 'cio/utils/sort';
import { ProductApp } from 'cio/utils/product-app-info';
import SessionService from 'cio/services/-session';

export type EnvironmentOption = AppController['environmentOptions'][number];

// The navbar collapses if the content width is below this value
// The value was picked by measuring the maximum window width
// before the content starts getting squished.
// TODO: The value could be reduced to 1140, which is the minimum
// window size before the layout starts breaking down, but that
// creates conflicts with global CSS media queries. We should look
// into changing them into container queries
const MIN_APP_FRAME_WIDTH = 1490;

// Routes added to FULL_SCREEN_ROUTES will collapse the main nav and remove the app header (breadcrumbs/help/notifications/etc)
const FULL_SCREEN_ROUTES = [
  'campaigns.campaign.setup',
  'campaigns.campaign.overview',
  'campaigns.campaign.loading',
  'campaigns.campaign_loading',
  'campaigns.welcome.create',
  'broadcasts.broadcast.setup',
  'broadcasts.broadcast.overview',
  'broadcasts.broadcast.loading',
  'broadcasts.broadcast_loading',
  'broadcasts.newsletter.setup',
  'composer',
  'transactional.message.setup',
  'transactional.message.overview',
  'workspace-settings.subscription_center.edit',
  'workspace-settings.subscription_center.branding_edit',
  'workspace-settings.subscription_center.localization.edit',
  'workspace-settings.subscription_center.topics.set_preferences.type',
  'in-app-messages.message',
];

const vwbActiveRoutes = [
  'campaigns.campaign.setup.workflow',
  'campaigns.campaign.overview.workflow',
  'broadcasts.broadcast.setup.workflow',
  'broadcasts.broadcast.overview.workflow',
];

// Routes added to COLLAPSE_ROUTES will only collapse the main nav when the screen size is under a certain threshold
const COLLAPSE_ROUTES = [
  'setup-guide',
  'object',
  'person',
  'data-index.type.item',
  'welcome',
  'workspace-settings.subscription_center.edit',
  'workspace-settings.objects.new',
  'workspace-settings.objects.edit',
  'workspace-settings.subscription_center.branding_edit',
  'workspace-settings.subscription_center.localization.edit',
  'workspace-settings.subscription_center.topics.set_preferences.type',
  'in-app-messages.message',
];

// Hides the trial banner and payment issue banner from the following pages
// Plans & Billing - Your Plan
// Plans & Billing - Available Plans - Journeys/Data Pipelines
// Product Activation - Experience/Goals
const HIDE_BANNER_ROUTES = [
  { routeName: 'settings.billing.your-plan', exactMatch: true, startsMatch: false },
  { routeName: 'settings.billing.available-plans', exactMatch: false, startsMatch: true },
  { routeName: 'settings.billing.checkout', exactMatch: true, startsMatch: false },
  { routeName: 'product-activation', exactMatch: false, startsMatch: true },
];

// It takes 250ms to fade in/out the app container based on the
// "is-faded-out" class opacity transition.
const ENVIRONMENT_FADE_TIME_IN_MS = 250;

const ALL_FULL_SCREEN_ROUTES = [...FULL_SCREEN_ROUTES, ...COLLAPSE_ROUTES];

export default class AppController extends Controller {
  // @ts-ignore queryParams in the array format is not typed
  queryParams = [
    { showSupport: { type: 'boolean' } },
    { showSupport: 'support' },
    { utmSource: 'utm_source' },
    { utmMedium: 'utm_medium' },
    { utmCampaign: 'utm_campaign' },
    { utmTerm: 'utm_term' },
    { utmContent: 'utm_content' },
  ];

  showSupport: true | null = null;
  utmSource = null;
  utmMedium = null;
  utmCampaign = null;
  utmTerm = null;
  utmContent = null;

  @service auth!: AuthService;
  @service session!: SessionService;
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service billing!: BillingService;
  @service confirmChanges!: ConfirmChangesService;
  @service('crumbs') crumbsService!: CrumbsService;
  @service environment!: EnvironmentService;
  @service eventBus!: EventBusService;
  @service masq!: MasqService;
  @service router!: RouterService;
  @service('releaseNotification') releaseNotification!: ReleaseNotificationService;
  @service hydra!: HydraService;

  declare model: EnvironmentModel[];

  get showMainNav() {
    return !this.hydra.isHydraEnabled;
  }

  get showUpgradeLink(): boolean {
    return this.account.current.suspendTrialAt != null || this.account.planFeatures.messaging.expiresAt != null;
  }

  get hideBanner() {
    let currentRouteName = this.router.currentRouteName ?? '';
    return HIDE_BANNER_ROUTES.some((route) => {
      let matches = false;
      if (route.exactMatch) {
        matches = currentRouteName === route.routeName;
      } else if (route.startsMatch) {
        matches = currentRouteName.startsWith(route.routeName);
      }
      return matches;
    });
  }

  forceFullScreen = false;
  isProductionLikeStaging = config.isProdStaging;
  isAppFrameWidthBelowThreshold = false;

  @bool('account.current.overdue') shouldDisplayOverdueMessage!: boolean;
  @bool('billing.billingInfo.lumpSum') isLumpSumPremiumCustomer!: boolean;
  @bool('billing.subscription.hasFailedOverageCharge') hasFailedOverageCharge!: boolean;

  nextEnvironment?: EnvironmentModel;

  constructor() {
    super(...arguments);

    this.router.on('routeDidChange', this.onRouteDidChange);
    this.router.on('routeWillChange', this.onRouteWillChange);
  }

  willDestroy() {
    super.willDestroy();

    // @ts-ignore TS complains about "off" not existing, but it does exist
    this.router.off('routeDidChange', this.onRouteDidChange);
    // @ts-ignore
    this.router.off('routeWillChange', this.onRouteWillChange);
  }

  @action
  didInsertAppFrame(element: HTMLDivElement) {
    if (this.hydra.isHydraEnabled) {
      // Hydra takes care of collapsing the nav based on browser width, no need
      // for the CIO UI to do this
      return;
    }

    let resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        requestAnimationFrame(() => {
          if (this.isDestroying || this.isDestroyed) {
            return;
          }
          this.set('isAppFrameWidthBelowThreshold', entry.contentRect.width < MIN_APP_FRAME_WIDTH);
        });
      }
    });

    resizeObserver.observe(element);

    registerDestructor(this, () => {
      resizeObserver.disconnect();
    });
  }

  // See explanation in setEnvironmentTask on why we need this route change
  // callback
  @action
  onRouteDidChange() {
    if (this.hidePageWrapperWhenChangingEnvironments || this.hidePageContentWhenChangingEnvironments) {
      this.routeChanged = true;
    }

    // we store last visited paged for redirecting back after login when session expires
    this.session.set('lastVisitedRoute', window.location.pathname);
    if (this.hydra.isHydraEnabled) {
      setNavCollapsed(this.isFullScreen || this.collapseNavigation);
    }
  }

  @action
  onRouteWillChange(transition: Transition) {
    if (this.hydra.isHydraEnabled) {
      let targetEnvironmentId =
        transition.to?.find((routeInfo) => routeInfo.name === 'environment')?.params.environment_id ?? null;

      // When transitioning to a different environment, we want to hide the main
      // outlet content until the new environment is loaded. This is to avoid any
      // issues with the old environment or any model content still being visible
      // as we transition.
      // For example, some LinkTo components might break as the environment's
      // models are being unloaded.
      if (
        targetEnvironmentId != null &&
        this.environment.current.id != null &&
        this.environment.current.id !== targetEnvironmentId
      ) {
        // Hides page content (but not the entire page wrapper). We want to keep
        // the WorkspaceHeader component intact, so that the "Need help" and
        // "Notifications" dropdowns in the Hydra header don't disappear when
        // switching environments. Otherwise the dropdowns would flash out and
        // back in, which makes for an ugly UX.
        this.hidePageContentWhenChangingEnvironments = true;
        this.router.on('routeDidChange', this.onEnvironmentChanged);
      }

      let isTransitioningToFullScreenRoute = ALL_FULL_SCREEN_ROUTES.some(
        (route) => transition.to?.name.startsWith(route) ?? false,
      );

      setNavCollapsed(isTransitioningToFullScreenRoute);
    }
  }

  @action
  onEnvironmentChanged() {
    this.router.off('routeDidChange', this.onEnvironmentChanged);
    this.hidePageContentWhenChangingEnvironments = false;
  }

  get environments(): EnvironmentModel[] {
    return this.model ?? [];
  }

  get environmentOptions() {
    return sortByCaseInsensitive(
      this.environments.filter((environment) => !environment.isNew),
      ['name'],
    ).map((environment) => {
      return {
        model: environment,
        label: environment.name,
        value: environment.id,
        deliveryLabel: environment.deliveryLabel,
      };
    });
  }

  @computed('router.currentRouteName')
  get isRouteAlwaysFullScreen() {
    return FULL_SCREEN_ROUTES.some((route) => this.router.isActive(route));
  }

  @or('forceFullScreen', 'isRouteAlwaysFullScreen') isFullScreen!: boolean;

  @computed('isFullScreen', 'isAppFrameWidthBelowThreshold')
  get shouldCollapseNavBar() {
    return this.collapseNavigation || this.isFullScreen || this.isAppFrameWidthBelowThreshold;
  }

  @computed('router.currentRouteName')
  get isEnvironmentsRouteActive() {
    return this.router.isActive('environments');
  }

  get showColoredBorder() {
    return this.nextEnvironment;
  }

  @computed('router.currentRouteName')
  get isCampaignOverview() {
    let { currentRouteName } = this.router;
    return (
      currentRouteName.includes('campaigns.campaign.overview') ||
      currentRouteName.includes('broadcasts.broadcast.overview')
    );
  }

  get isJourneysActive() {
    return this.account.accessibleProducts.includes(ProductApp.JOURNEYS);
  }

  @computed('router.currentRouteName')
  get isVWBActive() {
    return vwbActiveRoutes.some((route) => this.router.isActive(route));
  }

  @computed('hydra.isHydraEnabled', 'router.currentRouteName')
  get lockViewPort() {
    if (this.hydra.isHydraEnabled) {
      return false;
    }

    // On certain pages, we require the viewport to be "locked" so that some elements do not scroll with the page.
    // e.g on vwb pages. This is a list of routes where we need to impose to have a full screen
    let vwBAndComposerRoutes = [
      ...vwbActiveRoutes,
      'campaigns.campaign.setup.workflow.loading',
      'campaigns.campaign.overview.workflow.loading',
      'composer',
    ];
    return vwBAndComposerRoutes.some((route) => this.router.isActive(route));
  }

  @computed('router.currentRouteName')
  get collapseNavigation() {
    return COLLAPSE_ROUTES.any((route) => this.router.isActive(route));
  }

  get lastEnvironmentId() {
    return this.environment.lastEnvironmentId;
  }

  @tracked hidePageWrapperWhenChangingEnvironments = false;
  @tracked hidePageContentWhenChangingEnvironments = false;
  @tracked isFadingContainer = false;
  @tracked routeChanged = false;

  setEnvironmentTask = task(async (environment: EnvironmentModel) => {
    // In normal circumstances, the ConfirmChanges mixin fires in the
    // "willTransition" action. However, the workspace change step first fades
    // out the container, and only afterwards attempts the transition - if
    // there are unsaved changes, it just fades back without changing
    // workspaces or showing a "save changes" modal, because the transition is
    // getting aborted.
    // This detects whether the current route has the ConfirmChanges mixin,
    // and whether there are any unsaved changes. If so, it'll publish the
    // CONFIRM_TRANSITION event, which will be caught by the CioSaveBar
    // component.
    // We also include a fake "transition" object - the CioSaveBar calls the
    // "retry" method after discarding or saving unsaved changes, which will
    // resume the workspace switch here.
    let confirmChangesManager = this.confirmChanges.getManagerInstance();

    if (confirmChangesManager != null) {
      if (confirmChangesManager.isDirty) {
        this.eventBus.publish(CONFIRM_TRANSITION, {
          retry: () => {
            this.setEnvironmentTask.perform(environment);
            return;
          },
        });
        return;
      } else {
        // set local "nextEnvironment": it will be passed to app header to initiate env. color change animation
        this.set('nextEnvironment', environment);
      }
    }

    this.isFadingContainer = true;
    // wait to allow a smooth fade out of the content (opacity change from
    // the "is-faded-out" class)
    if (!Ember.testing) {
      await timeout(ENVIRONMENT_FADE_TIME_IN_MS);
    }
    // Force the outlet to be hidden, destroying all components within (to
    // prevent any components from accessing data we're about to unload).
    this.hidePageWrapperWhenChangingEnvironments = true;
    await waitForQueue('afterRender');
    this.routeChanged = false;

    // save the environment to the user model so its loaded on the next sign in
    // skip if env is in a masqed account: saving it can cause environment/user account mismatch, redirect problems later
    if (!this.masq.isOn) {
      this.setUserLastEnvironmentTask.perform(environment);
    }

    // app container is faded out: switch environments
    this.router.transitionTo('dashboard', environment.id);

    let now = unixNowMilliseconds();

    // We need to wait until the actual transition is done before we render the
    // outlet again. If we don't wait, the old view might still be present, and
    // it may want to render components accessing models that are being
    // unloaded.
    // Ember's Transition class is supposed to be .thenable, and act as a
    // promise we could await. We should be able to just `await
    // this.router.transitionTo ...`, but in practice that doesn't work
    // reliably.
    // In my testing, the transition's promise either finished too early, or
    // sometimes never finished (?!).
    // Instead, we'll use a hack. We have a callback on the router service's
    // routeDidChange event. That seems to act way more reliably.
    // Before transitioning, we set `this.routeChanged` to false. Once the
    // transition finished, the callback changes that back to true, and this
    // timeout loop finishes.
    // I'm also adding a safety - if this loop takes longer than 60 seconds,
    // it'll exit anyway just in case. This step may take a while, as
    // transitioning to the new environment retrieves a fair bot of data from
    // the API.
    while (!this.routeChanged && unixNowMilliseconds() - now < 60000) {
      await timeout(50);
    }
    await waitForQueue('afterRender');

    // un-set local nextEnvironment: app header can use updated currentEnvironment now
    this.set('nextEnvironment', null);

    // track that the workspace was switched
    this.analytics.track('workspace', 'switched');

    // Show outlet again
    this.hidePageWrapperWhenChangingEnvironments = false;
    // Alow for runloop to render things
    await waitForQueue('afterRender');
    // Fade in content
    this.isFadingContainer = false;
  });

  setUserLastEnvironmentTask = task(async (environment: EnvironmentModel) => {
    if (this.hydra.isHydraEnabled) {
      return;
    }

    let { currentUser: user } = this.account.current;

    user.set('lastEnvironment', environment);
    try {
      await user.save();
    } catch (e) {
      this.apiErrors.report(e);
      user.rollbackAttributes();
    }
  });

  resendConfirmationEmailTask = task(async () => {
    this.analytics.track('signup', 'resend_confirmation_clicked');

    if (this.auth.user == null) {
      this.alerts.error(
        'app.resend-confirmation-email-task.user-missing',
        'Unable to find current user. Refresh the page and try again.',
      );
      return;
    }

    try {
      await this.auth.user.resendConfirmationEmail({});
      this.alerts.success(
        'resend-confirmation',
        `Confirmation email sent to ${this.auth.user?.email ?? 'unknown email'}`,
      );
    } catch (response) {
      this.apiErrors.report(response);
    }
  });

  @action
  setFrameBorder() {
    if (this.hydra.isHydraEnabled) {
      return;
    }

    let environment = this.nextEnvironment ?? this.environment.current;

    if (environment?.hexCode) {
      document.documentElement.style.setProperty('--app-border-width', '12px');
      document.documentElement.style.setProperty('--app-border-color', environment.hexCodeWithDefault);
    } else {
      document.documentElement.style.setProperty('--app-border-width', '0px');
      document.documentElement.style.setProperty('--app-border-color', '');
    }
  }

  @action
  setAccountBannersHeight(element: HTMLDivElement) {
    document.documentElement.style.setProperty('--account-banners-height', `${element.clientHeight}px`);
  }

  get isTesting() {
    return Ember.testing;
  }

  get shouldShowPaymentIssueAlert() {
    return (
      this.account.current?.accountState === 'overdue' ||
      (this.billing.billingInfo?.pinnedStartDate !== 0 &&
        this.billing.billingInfo?.pinnedPlan !== null &&
        this.billing.subscription?.active === false) ||
      (this.billing.subscription?.active === true && this.billing.subscription?.hasFailedOverageCharge === true) ||
      this.billing.billingInfo?.overdue === true
    );
  }

  get showCdpBanner() {
    // only show the promo banner on the Available Plans page
    let { currentRouteName } = this.router;
    return currentRouteName.indexOf('settings.billing.available-plans') === 0;
  }
}

declare module '@ember/controller' {
  interface Registry {
    app: AppController;
  }
}
